import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

type ButtonConfig = {
    label: string;
    labelColor: string;
    buttonColor: string;
    hoverColor?: string;
    onPress: () => void;
    isPressable?: boolean;
};

type CustomDialogProps = {
    isOpenDialog: boolean;
    onClose: () => void;
    header: React.ReactNode;
    contents: React.ReactNode;
    buttonList: ButtonConfig[];
};

/**
 * ConfirmDialogは、ユーザーに確認や選択を促すための汎用ダイアログコンポーネントです。
 *
 * @param isOpenDialog ダイアログの表示状態（trueで表示、falseで非表示）
 *   例: true
 * @param onClose ダイアログを閉じる際に呼ばれる関数
 *   例: () => setOpen(false)
 * @param header ダイアログのタイトル部分に表示するReactノード
 *   例: <span>削除の確認</span>
 * @param contents ダイアログの本文に表示するReactノード
 *   例: <span>本当に削除しますか？</span>
 * @param buttonList ボタン設定の配列。各ボタンのラベルや色、押下時の処理などを指定
 *   例: [
 *     {
 *       label: "キャンセル",
 *       onPress: handleCancel,
 *       buttonColor: "#CCCCCC",
 *       labelColor: "#000000",
 *       hoverColor: "#AAAAAA",
 *       isPressable: true
 *     },
 *     {
 *       label: "削除",
 *       onPress: handleDelete,
 *       buttonColor: "#FF0000",
 *       labelColor: "#FFFFFF",
 *       hoverColor: "#CC0000",
 *       isPressable: true
 *     }
 *   ]
 */
export const ConfirmDialog: React.FC<CustomDialogProps> = ({
    isOpenDialog,
    onClose,
    header,
    contents,
    buttonList
}) => (
    <Dialog open={isOpenDialog} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {header}
        </DialogTitle>
        <DialogContent>
            {contents}
        </DialogContent>
        <DialogActions>
            {buttonList.map((button, idx) => (
                <Button
                    key={button.label + idx}
                    onClick={button.onPress}
                    variant="contained"
                    sx={{
                        bgcolor: button.buttonColor,
                        color: button.labelColor,
                        boxShadow: 'none',
                        '&:hover': button.hoverColor && {
                            bgcolor: button.hoverColor,
                        },
                    }}
                    disabled={!button?.isPressable}
                >
                    {button.label}
                </Button>
            ))}
        </DialogActions>
    </Dialog>
);

export default ConfirmDialog;
